import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import PowerSettingsNewRoundedIcon from '@mui/icons-material/PowerSettingsNewRounded';
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import SettingsRemoteRoundedIcon from '@mui/icons-material/SettingsRemoteRounded';
import WifiRoundedIcon from '@mui/icons-material/WifiRounded';
import Filter1RoundedIcon from '@mui/icons-material/Filter1Rounded';
import Filter2RoundedIcon from '@mui/icons-material/Filter2Rounded';
import mqtt from "mqtt";
import { getAllVM } from "../../api/routes";

const ringPulse = keyframes`
    0% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(120, 241, 92, 0.45);
    }
    70% {
        transform: scale(1.06);
        box-shadow: 0 0 0 18px rgba(120, 241, 92, 0);
    }
    100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(120, 241, 92, 0);
    }
`;

const MQTT_OPTIONS = {
    username: 'operator_ws',
    password: 'Q4ddoje88D_u1cUsTUHwI30dtbwdj6LP',
    rejectUnauthorized: true,
    reconnectPeriod: 2000,
};

const client_left = mqtt.connect('wss://ivm9.in:9001', MQTT_OPTIONS);

client_left.on('connect', () => {
    console.log('Connected to MQTT broker left');
});

client_left.on('error', (error) => {
    console.error('MQTT error:', error);
});

client_left.on('close', () => {
    console.log('MQTT connection closed');
});

export function LeftGrid({ user_id }) {
    const [loading, setLoading] = useState(true);
    const [opVm, setOpVM] = useState([]);
    const [light, setLight] = useState(false);
    const [alarm, setAlarm] = useState(false);
    const [f1, setF1] = useState(false);
    const [f2, setF2] = useState(false);
    const [selectedVMId, setSelectedVMId] = useState('');
    const [now, setNow] = useState(Date.now());

    const isMachineOnline = useCallback((vm) => vm.status === 10, []);
    const hasIncomingCall = useCallback((vm) => isMachineOnline(vm) && vm.call_status === 10, [isMachineOnline]);
    const hasActiveCall = useCallback((vm) => isMachineOnline(vm) && vm.call_status === 5, [isMachineOnline]);
    const hasOpenCallSession = useCallback((vm) => isMachineOnline(vm) && [5, 10, 20].includes(vm.call_status), [isMachineOnline]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const vm = await getAllVM();
            const operatorVMs = vm.filter((machine) => machine.operator_assigned_id === user_id);
            setOpVM(operatorVMs);
        } catch (error) {
            console.log("error", error);
        } finally {
            setLoading(false);
        }
    }, [user_id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        opVm.forEach((vm) => {
            client_left.subscribe(`vm/${vm.guid}/call/status`);
            client_left.subscribe(`heartbeat/vendingmachine/alive/${vm.guid}`);
            client_left.subscribe(`heartbeat/vendingmachine/dead/${vm.guid}`);
        });
    }, [opVm]);

    useEffect(() => {
        const handler = (topic, message) => {
            try {
                const payload = JSON.parse(message.toString());
                if (payload.type === "call_status" || payload.type === "status") {
                    setTimeout(() => {
                        fetchData();
                    }, 300);
                }
            } catch (error) {
                console.error('Failed to parse MQTT payload', topic, error);
            }
        };

        client_left.on('message', handler);
        return () => {
            if (typeof client_left.off === 'function') {
                client_left.off('message', handler);
            }
        };
    }, [fetchData]);

    useEffect(() => {
        const timer = window.setInterval(() => {
            setNow(Date.now());
        }, 1000);

        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        if (selectedVMId && !opVm.some((vm) => vm.guid === selectedVMId)) {
            setSelectedVMId('');
        }
    }, [opVm, selectedVMId]);

    const selectedVM = useMemo(
        () => opVm.find((vm) => vm.guid === selectedVMId) || null,
        [opVm, selectedVMId]
    );

    const summary = useMemo(() => ({
        online: opVm.filter((vm) => vm.status === 10).length,
        ringing: opVm.filter((vm) => vm.status === 10 && vm.call_status === 10).length,
        active: opVm.filter((vm) => vm.status === 10 && vm.call_status === 5).length,
        waiting: opVm.filter((vm) => vm.status === 10 && vm.call_status === 20).length,
    }), [opVm]);

    const selectMachine = (vmId) => {
        setSelectedVMId(vmId);
    };

    const updateCallStatus = (id, status) => {
        setSelectedVMId(id);
        client_left.publish(`vm/${id}/call/status`, JSON.stringify({
            type: "call_status",
            value: String(status),
            vm_id: id,
        }));
    };

    const publishMachineAction = (topic, message) => {
        if (!selectedVM) {
            // eslint-disable-next-line no-restricted-globals
            confirm(`Please select a vending machine for the action`);
            return;
        }

        if (client_left.connected) {
            client_left.publish(topic, message, (error) => {
                if (error) {
                    console.error('Publish error:', error);
                }
            });
        } else {
            console.warn('MQTT client is not connected');
        }
    };

    const lightON = () => {
        const nextLight = !light;
        publishMachineAction(`vm/pi/${selectedVM?.guid}/light`, nextLight ? "on" : "off");
        setLight(nextLight);
    };

    const onAlarm = () => {
        const nextAlarm = !alarm;
        publishMachineAction(`vm/pi/${selectedVM?.guid}/alarm`, nextAlarm ? "on" : "off");
        setAlarm(nextAlarm);
    };

    const onF1 = () => {
        const nextF1 = !f1;
        publishMachineAction(`vm/pi/${selectedVM?.guid}/f1`, nextF1 ? "on" : "off");
        setF1(nextF1);
    };

    const onF2 = () => {
        const nextF2 = !f2;
        publishMachineAction(`vm/pi/${selectedVM?.guid}/f2`, nextF2 ? "on" : "off");
        setF2(nextF2);
    };

    const formatDuration = (dateValue) => {
        if (!dateValue) {
            return '--';
        }

        const start = new Date(dateValue).getTime();
        if (Number.isNaN(start)) {
            return '--';
        }

        const diffInSeconds = Math.max(0, Math.floor((now - start) / 1000));
        const hours = Math.floor(diffInSeconds / 3600);
        const minutes = Math.floor((diffInSeconds % 3600) / 60);
        const seconds = diffInSeconds % 60;

        if (hours > 0) {
            return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
        }

        return `${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
    };

    const formatDurationFromSeconds = (secondsValue) => {
        if (secondsValue === null || secondsValue === undefined || Number.isNaN(Number(secondsValue))) {
            return '--';
        }

        const diffInSeconds = Math.max(0, Math.floor(Number(secondsValue)));
        const hours = Math.floor(diffInSeconds / 3600);
        const minutes = Math.floor((diffInSeconds % 3600) / 60);
        const seconds = diffInSeconds % 60;

        if (hours > 0) {
            return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
        }

        return `${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
    };

    const getCallDurationLabel = (vm) => {
        const metadata = vm?.metadata || {};

        if ([5, 10, 20].includes(vm.call_status)) {
            return formatDuration(metadata.current_call_started_at || metadata.last_call_started_at);
        }

        if (metadata.last_call_duration_seconds !== undefined) {
            return formatDurationFromSeconds(metadata.last_call_duration_seconds);
        }

        return '--';
    };

    const getMachinePresentation = (vm) => {
        if (!isMachineOnline(vm)) {
            return {
                accent: 'error.main',
                chipLabel: 'Offline',
                chipIcon: <PowerSettingsNewRoundedIcon fontSize="small" />,
                chipVariant: 'filled',
                heroIcon: <PowerSettingsNewRoundedIcon sx={{ fontSize: 38, color: '#fff' }} />,
                helper: 'Machine is not reachable',
                heroLabel: 'OFFLINE',
                primaryLabel: 'OFFLINE',
                primaryColor: 'error',
                primaryEnabled: false,
                heroTone: 'error',
                statusText: 'No active call session',
                durationLabel: 'Last call',
            };
        }

        if (hasActiveCall(vm)) {
            return {
                accent: 'info.main',
                chipLabel: 'On call',
                chipIcon: <PhoneInTalkRoundedIcon fontSize="small" />,
                chipVariant: 'filled',
                heroIcon: <PhoneInTalkRoundedIcon sx={{ fontSize: 38, color: '#fff' }} />,
                helper: 'Customer is connected',
                heroLabel: 'ON CALL',
                primaryLabel: 'ON CALL',
                primaryColor: 'primary',
                primaryEnabled: false,
                heroTone: 'info',
                statusText: 'Dispensing is available',
                durationLabel: 'Call duration',
            };
        }

        if (hasIncomingCall(vm)) {
            return {
                accent: 'success.main',
                chipLabel: 'Incoming call',
                chipIcon: <PhoneInTalkRoundedIcon fontSize="small" />,
                chipVariant: 'filled',
                heroIcon: <PhoneInTalkRoundedIcon sx={{ fontSize: 38, color: '#fff' }} />,
                helper: 'Blinks until the operator accepts',
                heroLabel: 'ACCEPT CALL',
                primaryLabel: 'ACCEPT CALL',
                primaryColor: 'success',
                primaryEnabled: true,
                heroTone: 'success',
                statusText: 'Waiting for operator action',
                durationLabel: 'Ring duration',
            };
        }

        if (vm.call_status === 20) {
            return {
                accent: 'warning.main',
                chipLabel: 'Waiting',
                chipIcon: <HourglassTopRoundedIcon fontSize="small" />,
                chipVariant: 'filled',
                heroIcon: <HourglassTopRoundedIcon sx={{ fontSize: 38, color: '#fff' }} />,
                helper: 'Call is on operator wait',
                heroLabel: 'WAITING',
                primaryLabel: 'ACCEPT CALL',
                primaryColor: 'warning',
                primaryEnabled: true,
                heroTone: 'warning',
                statusText: 'Customer session paused',
                durationLabel: 'Wait duration',
            };
        }

        return {
            accent: 'success.main',
            chipLabel: 'Online',
            chipIcon: <WifiRoundedIcon fontSize="small" />,
                chipVariant: 'outlined',
                heroIcon: <WifiRoundedIcon sx={{ fontSize: 38, color: '#fff' }} />,
                helper: 'Ready for the next customer',
            heroLabel: 'ONLINE',
            primaryLabel: 'ONLINE',
            primaryColor: 'success',
            primaryEnabled: false,
            heroTone: 'success',
            statusText: 'No call in progress',
            durationLabel: 'Last call',
        };
    };

    const renderSummaryCard = (title, value, subtitle, color) => (
        <Grid item xs={6} sm={6} lg={6} xl={3} key={title}>
            <Paper
                sx={(theme) => ({
                    p: { xs: 1, md: 1.4 },
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: alpha(theme.palette[color].main, 0.25),
                    bgcolor: alpha(theme.palette[color].main, theme.palette.mode === 'dark' ? 0.12 : 0.07),
                    boxShadow: 'none',
                    minHeight: { xs: 84, md: 102 },
                })}
            >
                <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', md: '0.75rem' } }}>
                    {title}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.02, mb: 0.25, fontSize: { xs: '1.7rem', md: '2.125rem' } }}>
                    {value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.72rem', md: '0.875rem' }, lineHeight: 1.25 }}>
                    {subtitle}
                </Typography>
            </Paper>
        </Grid>
    );

    return (
        <Box sx={{ p: { xs: 0.9, md: 1.75 }, height: { xs: '100%', xl: '100%' }, overflow: { xs: 'hidden', xl: 'hidden' } }}>
            <Stack spacing={1.5} sx={{ height: { xs: 'auto', xl: '100%' } }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1, fontSize: { xs: '1.8rem', md: '2.125rem' } }}>
                        IVM9 CONSOLE
                    </Typography>
                </Box>

                <Grid container spacing={{ xs: 0.8, md: 1.25 }}>
                    {renderSummaryCard('Online', summary.online, `${summary.ringing} ringing, ${summary.active} on call`, 'success')}
                    {renderSummaryCard('Waiting', summary.waiting, 'Calls parked by the operator', 'warning')}
                    {renderSummaryCard('Assigned', opVm.length, 'Machines mapped to this operator', 'primary')}
                    <Grid item xs={6} sm={6} lg={6} xl={3}>
                        <Paper
                            sx={(theme) => ({
                                p: { xs: 1, md: 1.4 },
                                borderRadius: 2.5,
                                border: '1px solid',
                                borderColor: selectedVM ? alpha(theme.palette.info.main, 0.3) : 'divider',
                                bgcolor: selectedVM
                                    ? alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.12 : 0.06)
                                    : 'background.paper',
                                boxShadow: 'none',
                                minHeight: { xs: 84, md: 102 },
                            })}
                        >
                            <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', md: '0.75rem' } }}>
                                Selected Machine
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, minHeight: { xs: 24, md: 28 }, lineHeight: 1.15, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                                {selectedVM ? selectedVM.name : 'None selected'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: { xs: '0.72rem', md: '0.875rem' }, lineHeight: 1.25 }}>
                                {selectedVM ? 'Machine controls below target this machine.' : 'Click a machine card to arm controls.'}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        pr: { xs: 0.25, xl: 0.5 },
                    }}
                >
                    <Grid container spacing={1} justifyContent={{ xs: 'stretch', sm: 'flex-start' }}>
                        {loading && [...Array(4)].map((_, index) => (
                            <Grid item xs={6} sm={4} md={3} lg={3} xl={3} key={`machine-skeleton-${index}`} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Paper sx={{ p: 1.4, borderRadius: 3, bgcolor: 'background.paper', width: '100%', maxWidth: 176 }}>
                                    <Skeleton variant="text" width="55%" height={36} />
                                    <Skeleton variant="rounded" width="100%" height={40} sx={{ mt: 1.2 }} />
                                    <Skeleton variant="rounded" width="100%" height={40} sx={{ mt: 1 }} />
                                    <Skeleton variant="rounded" width="100%" height={40} sx={{ mt: 1 }} />
                                    <Skeleton variant="text" width="70%" sx={{ mt: 1.2 }} />
                                </Paper>
                            </Grid>
                        ))}

                        {!loading && opVm.map((vm) => {
                        const presentation = getMachinePresentation(vm);
                        const isSelected = selectedVMId === vm.guid;
                        const showRingAnimation = hasIncomingCall(vm);
                        const showDuration = [5, 10, 20].includes(vm.call_status) || vm?.metadata?.last_call_duration_seconds !== undefined;

                            return (
                                <Grid item xs={6} sm={4} md={3} lg={3} xl={3} key={vm.guid} sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Paper
                                        onClick={() => selectMachine(vm.guid)}
                                        sx={(theme) => ({
                                            position: 'relative',
                                            p: 1.35,
                                            minHeight: 248,
                                            width: '100%',
                                            maxWidth: 176,
                                            borderRadius: 1.5,
                                            cursor: 'pointer',
                                            overflow: 'hidden',
                                            border: '1px solid',
                                            borderColor: isSelected
                                                ? alpha(theme.palette.info.main, 0.55)
                                                : alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.18 : 0.08),
                                            background: theme.palette.mode === 'dark'
                                                ? `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.06)} 0%, ${alpha(theme.palette.common.white, 0.02)} 100%)`
                                                : 'linear-gradient(180deg, #d2cccc 0%, #b9b0b0 100%)',
                                            color: theme.palette.mode === 'dark' ? theme.palette.common.white : '#fff',
                                            boxShadow: isSelected
                                                ? `0 10px 22px ${alpha(theme.palette.info.main, 0.22)}`
                                                : `0 10px 20px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.22 : 0.1)}`,
                                            transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 14px 26px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.24 : 0.14)}`,
                                            },
                                        })}
                                    >
                                        <Box
                                            sx={(theme) => ({
                                                position: 'absolute',
                                                inset: 0,
                                                opacity: 0.9,
                                                background: `radial-gradient(circle at top right, ${alpha(theme.palette[presentation.primaryColor].light, 0.28)} 0%, transparent 40%)`,
                                                pointerEvents: 'none',
                                            })}
                                        />

                                        <Stack spacing={0.9} sx={{ position: 'relative', height: '100%' }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: 800,
                                                            lineHeight: 1.08,
                                                            letterSpacing: '-0.03em',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            fontSize: '0.9rem',
                                                        }}
                                                    >
                                                        {vm.name}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 0.35, color: alpha('#fff', 0.82), lineHeight: 1.25, fontSize: '0.72rem' }}>
                                                        {presentation.helper}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    icon={presentation.chipIcon}
                                                    label={presentation.chipLabel}
                                                    variant={presentation.chipVariant}
                                                    sx={(theme) => ({
                                                        height: 24,
                                                        borderRadius: 2.25,
                                                        fontWeight: 700,
                                                        fontSize: '0.64rem',
                                                        color: '#fff',
                                                        '& .MuiChip-label': {
                                                            px: 0.75,
                                                        },
                                                        borderColor: alpha(theme.palette[presentation.primaryColor].light, 0.5),
                                                        bgcolor: presentation.chipVariant === 'filled'
                                                            ? alpha(theme.palette[presentation.primaryColor].main, 0.32)
                                                            : alpha(theme.palette.common.white, 0.08),
                                                        '& .MuiChip-icon': {
                                                            color: '#fff',
                                                        },
                                                    })}
                                                />
                                            </Stack>

                                            <Stack alignItems="center" spacing={0.55} sx={{ pt: 0.1 }}>
                                                <Box
                                                    sx={(theme) => ({
                                                        width: 46,
                                                        height: 46,
                                                        borderRadius: '50%',
                                                        display: 'grid',
                                                        placeItems: 'center',
                                                        bgcolor: alpha(theme.palette[presentation.primaryColor].main, showRingAnimation ? 0.95 : 0.85),
                                                        border: `2px solid ${alpha('#fff', 0.24)}`,
                                                        animation: showRingAnimation ? `${ringPulse} 1.6s infinite` : 'none',
                                                    })}
                                                >
                                                    {presentation.heroIcon}
                                                </Box>

                                                <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontSize: '0.88rem' }}>
                                                    {presentation.heroLabel}
                                                </Typography>

                                                <Typography variant="body2" sx={{ color: alpha('#fff', 0.82), textAlign: 'center', minHeight: 16, lineHeight: 1.2, fontSize: '0.72rem' }}>
                                                    {presentation.statusText}
                                                </Typography>

                                                {showDuration && (
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.68), textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.58rem' }}>
                                                            {presentation.durationLabel}
                                                        </Typography>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
                                                            {getCallDurationLabel(vm)}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Stack>

                                            <Stack spacing={0.5} sx={{ mt: 'auto' }}>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    size="large"
                                                    color={presentation.primaryColor}
                                                    onClick={() => presentation.primaryEnabled && updateCallStatus(vm.guid, 5)}
                                                    disabled={!presentation.primaryEnabled}
                                                    sx={{
                                                        py: 0.55,
                                                        minHeight: 30,
                                                        borderRadius: 2.25,
                                                        fontWeight: 800,
                                                        fontSize: '0.78rem',
                                                        '&.Mui-disabled': {
                                                            opacity: 1,
                                                            bgcolor: (theme) => alpha(theme.palette[presentation.primaryColor].main, 0.72),
                                                            color: '#fff',
                                                        },
                                                    }}
                                                >
                                                    {presentation.primaryLabel}
                                                </Button>

                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    size="large"
                                                    color="warning"
                                                    onClick={() => updateCallStatus(vm.guid, vm.call_status === 20 ? 5 : 20)}
                                                    disabled={!hasOpenCallSession(vm)}
                                                    sx={{
                                                        py: 0.55,
                                                        minHeight: 30,
                                                        borderRadius: 2.25,
                                                        fontWeight: 800,
                                                        fontSize: '0.78rem',
                                                        '&.Mui-disabled': {
                                                            opacity: 1,
                                                            bgcolor: alpha('#fff', 0.2),
                                                            color: alpha('#fff', 0.62),
                                                        },
                                                    }}
                                                >
                                                    {vm.call_status === 20 ? 'ACCEPT FROM WAIT' : 'WAIT'}
                                                </Button>

                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    size="large"
                                                    color="error"
                                                    onClick={() => updateCallStatus(vm.guid, 0)}
                                                    disabled={!hasOpenCallSession(vm)}
                                                    sx={{
                                                        py: 0.55,
                                                        minHeight: 30,
                                                        borderRadius: 2.25,
                                                        fontWeight: 800,
                                                        fontSize: '0.78rem',
                                                        '&.Mui-disabled': {
                                                            opacity: 1,
                                                            bgcolor: alpha('#fff', 0.2),
                                                            color: alpha('#fff', 0.62),
                                                        },
                                                    }}
                                                >
                                                    END CALL
                                                </Button>
                                            </Stack>

                                            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.4} sx={{ pt: 0.1 }}>
                                                {isSelected ? (
                                                    <RadioButtonCheckedRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />
                                                ) : (
                                                    <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 16, color: alpha('#fff', 0.85) }} />
                                                )}
                                                <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'center', fontSize: '0.68rem' }}>
                                                    {isSelected ? 'Selected for machine controls' : 'Click card to select machine'}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                </Grid>
                            );
                        })}

                        {!loading && opVm.length === 0 && (
                            <Grid item xs={12}>
                                <Paper sx={{ p: 4, borderRadius: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        No machines assigned
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Assign a vending machine to this operator to enable call handling and controls.
                                    </Typography>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                </Box>

                <Paper
                    sx={(theme) => ({
                        p: { xs: 1, md: 1.6 },
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.background.paper, 0.92)
                            : '#f5f7fb',
                        boxShadow: 'none',
                    })}
                >
                    <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ mb: 2 }}
                    >
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                Machine Controls
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3, fontSize: { xs: '0.74rem', md: '0.875rem' } }}>
                                {selectedVM ? `Remote actions for ${selectedVM.name}` : 'Select a machine card to enable remote controls.'}
                            </Typography>
                        </Box>
                        <Chip
                            icon={<SettingsRemoteRoundedIcon />}
                            label={selectedVM ? selectedVM.name : 'No machine selected'}
                            color={selectedVM ? 'info' : 'default'}
                            variant={selectedVM ? 'filled' : 'outlined'}
                            sx={{ alignSelf: { xs: 'flex-start', lg: 'center' }, borderRadius: 2.5, fontWeight: 700, height: { xs: 26, md: 32 }, '& .MuiChip-label': { px: { xs: 0.8, md: 1.5 }, fontSize: { xs: '0.68rem', md: '0.8rem' } } }}
                        />
                    </Stack>

                    <Divider sx={{ mb: { xs: 1, md: 1.5 } }} />

                    <Grid container spacing={{ xs: 0.75, md: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                onClick={lightON}
                                disabled={!selectedVM}
                                startIcon={<LightbulbRoundedIcon />}
                                sx={{ py: { xs: 0.7, md: 1 }, minHeight: { xs: 34, md: 40 }, borderRadius: 2.25, fontWeight: 700, fontSize: { xs: '0.72rem', md: '0.875rem' } }}
                            >
                                {!light ? "Lights On" : "Lights Off"}
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="warning"
                                onClick={onAlarm}
                                disabled={!selectedVM}
                                startIcon={<NotificationsActiveRoundedIcon />}
                                sx={{ py: { xs: 0.7, md: 1 }, minHeight: { xs: 34, md: 40 }, borderRadius: 2.25, fontWeight: 700, fontSize: { xs: '0.72rem', md: '0.875rem' } }}
                            >
                                {!alarm ? "Alarm On" : "Alarm Off"}
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                onClick={onF1}
                                disabled={!selectedVM}
                                startIcon={<Filter1RoundedIcon />}
                                sx={{ py: { xs: 0.7, md: 1 }, minHeight: { xs: 34, md: 40 }, borderRadius: 2.25, fontWeight: 700, fontSize: { xs: '0.72rem', md: '0.875rem' } }}
                            >
                                {!f1 ? "Enable F1" : "Disable F1"}
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="secondary"
                                onClick={onF2}
                                disabled={!selectedVM}
                                startIcon={<Filter2RoundedIcon />}
                                sx={{ py: { xs: 0.7, md: 1 }, minHeight: { xs: 34, md: 40 }, borderRadius: 2.25, fontWeight: 700, fontSize: { xs: '0.72rem', md: '0.875rem' } }}
                            >
                                {!f2 ? "Enable F2" : "Disable F2"}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Stack>
        </Box>
    );
}
